import { Modal as MuiModal } from "@mui/base/Modal";
import MuiDialog from "@mui/material/Dialog";
import Fade from "@mui/material/Fade";
import { useHandler } from "@redotech/react-util/hook";
import FeaturedLaptopIcon from "@redotech/redo-icons/featured-laptop-2.svg";
import FeaturedPackageCheckIcon from "@redotech/redo-icons/featured-package-check.svg";
import FeaturedRefreshIcon from "@redotech/redo-icons/featured-refresh-cw-3.svg";
import {
  bulletThreeSubtitleText,
  bulletThreeTitleText,
  defaultHeaderText,
  defaultSubheaderText,
} from "@redotech/redo-marketing-core-common/text-defaults/info-modal-default-text";
import { Coverage } from "@redotech/redo-return-coverage-common/coverage";
import { InfoModal } from "@redotech/redo-storefront-common/info-modal";
import { InfoModalVersion } from "@redotech/redo-storefront-common/info-modal-version";
import { translations } from "@redotech/redo-storefront-common/tos-translations";
import CheckCircle from "@redotech/redo-web/icon-old/check-circle.svg";
import Exchange from "@redotech/redo-web/icon-old/exchange.svg";
import Flag from "@redotech/redo-web/icon-old/flag.svg";
import ModalCloseButton from "@redotech/redo-web/icon-old/modal-close-button.svg";
import { ModalRootContext } from "@redotech/redo-web/modal-root";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import HTMLReactParser from "html-react-parser";
import * as React from "react";
import {
  ReactElement,
  ReactNode,
  Ref,
  memo,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  getPackageProtectionBullet,
  getReturnsBullet,
} from "./info-modal-bullets";
import * as modalV2 from "./modal-v2.module.css";
import * as modal from "./modal.module.css";
import { log } from "./monitoring/log";
import {
  getAccessibilityTranslation,
  getShopperLanguage,
} from "./storefront-locale";

function getTosTranslation() {
  const shopperLanguage = getShopperLanguage();

  return shopperLanguage && shopperLanguage in translations
    ? translations[shopperLanguage as keyof typeof translations]
    : translations.en;
}

function ReturnPolicyClause({
  url,
  translation,
}: {
  url?: string;
  translation: ReturnType<typeof getTosTranslation>;
}) {
  if (!url) {
    return <>{translation.coverageIsSubjectToMerchantReturnPolicy}</>;
  }
  return (
    <>
      {translation.coverageIsSubjectToPrefix}
      <a
        className={modalV2.link}
        href={url}
        rel="noopener noreferrer"
        target="_blank"
      >
        {translation.merchantReturnPolicyLinkText}
      </a>
      .
    </>
  );
}

function useManageEscapeKey({
  open,
  onClose,
  active,
}: {
  open: boolean;
  onClose?: () => void;
  active: boolean;
}) {
  // Keep the latest onClose without making it an effect dependency
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active || !open) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.type === "keyup") {
        onCloseRef.current?.();
      }
    };

    window.addEventListener("keydown", handleEscape, true);
    window.addEventListener("keyup", handleEscape, true);

    return () => {
      window.removeEventListener("keydown", handleEscape, true);
      window.removeEventListener("keyup", handleEscape, true);
    };
  }, [active, open]);
}

const Background = React.forwardRef<HTMLDivElement, any>(function Background(
  { className, ownerState, onClick, ...props },
  ref,
) {
  const blockPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const handleClickEventAndBlockPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onClick?.(e as React.MouseEvent);
  };

  // MUI Modal only listens to and blocks the click event, so we need to use custom behaviour
  return (
    <div
      className={modal.background}
      ref={ref}
      {...props}
      onClick={blockPropagation}
      onMouseDown={blockPropagation}
      onPointerDown={blockPropagation}
      onPointerUp={handleClickEventAndBlockPropagation}
      onTouchEnd={blockPropagation}
      onTouchMove={blockPropagation}
      onTouchStart={blockPropagation}
    />
  );
});

// Modal component that uses the top layer API (dialog element)
const ModalWithTopLayerApi = memo(function ModalWithTopLayerApi({
  open,
  onClose,
  container,
  children,
  disableFocusManagement = false,
  initialFocusRef,
}: {
  open: boolean;
  onClose?(): void;
  container: Element | null;
  children: ReactNode;
  disableFocusManagement?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
}) {
  const modalRef = useRef<HTMLDialogElement>(null);

  const handleModalStacking = (
    element: unknown | null,
    isOpen: boolean,
  ): boolean => {
    if (!element || !(element instanceof HTMLDialogElement)) {
      return false;
    }

    try {
      if (isOpen) {
        element.showModal();
      } else {
        element.close();
      }
    } catch (error: any) {
      log("Error showing/hiding modal with top layer API:", error);
    }
    return true;
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const attemptModalStacking = (delay: number) => {
      timeoutId = setTimeout(() => {
        if (handleModalStacking(modalRef.current, open)) {
          if (open) {
            initialFocusRef?.current?.focus();
          }
        } else {
          attemptModalStacking(delay + 100);
        }
      }, delay);
    };

    attemptModalStacking(0);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [initialFocusRef, open]);

  return (
    <MuiDialog
      className={modal.containerTopLayerApi}
      closeAfterTransition
      component="dialog"
      container={container}
      disableAutoFocus={disableFocusManagement}
      disableEnforceFocus={disableFocusManagement}
      disableRestoreFocus={disableFocusManagement}
      onClose={onClose}
      open={open}
      ref={modalRef as unknown as Ref<HTMLDivElement>}
      slots={{ backdrop: Background }}
    >
      {children}
    </MuiDialog>
  );
});

// Modal component that uses only z-index for stacking (old approach)
const ModalWithOnlyZIndex = memo(function ModalWithOnlyZIndex({
  open,
  onClose,
  container,
  children,
  disableFocusManagement = false,
}: {
  open: boolean;
  onClose?(): void;
  container: Element | null;
  children: ReactElement;
  disableFocusManagement?: boolean;
}) {
  return (
    <MuiModal
      className={modal.container}
      closeAfterTransition
      container={container}
      disableAutoFocus={disableFocusManagement}
      disableEnforceFocus={disableFocusManagement}
      disableRestoreFocus={disableFocusManagement}
      onClose={onClose}
      open={open}
      slots={{ backdrop: Background }}
    >
      {children}
    </MuiModal>
  );
});

// Feature-flagged modal component that selects between implementations
export const Modal = memo(function Modal({
  children,
  open,
  onClose,
  enableTopLayerModalApi = false,
  disableFocusManagement = false,
  initialFocusRef,
}: {
  children: ReactElement;
  open: boolean;
  onClose?(): void;
  enableTopLayerModalApi?: boolean;
  disableFocusManagement?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
}) {
  const root = useContext(ModalRootContext);

  // Because shopify's cart drawer listens to these events and will move behind us
  const blockPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  // On touch the browser sometimes doesn't synthesize a click inside the top-layer
  // dialog, so anchors never navigate — open the tapped one on pointerup instead.
  const handlePointerUp = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (e.pointerType !== "touch") {
      return;
    }
    const anchor = (e.target as HTMLElement).closest?.("a[href]");
    if (anchor instanceof HTMLAnchorElement) {
      window.open(anchor.href, anchor.target || "_self", "noopener,noreferrer");
    }
  };

  const modalContent = (
    <Fade in={open}>
      <div
        className={modalV2.modal}
        onClick={blockPropagation}
        onMouseDown={blockPropagation}
        onMouseUp={blockPropagation}
        onPointerDown={blockPropagation}
        onPointerUp={handlePointerUp}
        onTouchEnd={blockPropagation}
        onTouchStart={blockPropagation}
      >
        {children}
      </div>
    </Fade>
  );

  return enableTopLayerModalApi ? (
    <ModalWithTopLayerApi
      container={root}
      disableFocusManagement={disableFocusManagement}
      initialFocusRef={initialFocusRef}
      onClose={onClose}
      open={open}
    >
      {modalContent}
    </ModalWithTopLayerApi>
  ) : (
    <ModalWithOnlyZIndex
      container={root}
      disableFocusManagement={disableFocusManagement}
      onClose={onClose}
      open={open}
    >
      {modalContent}
    </ModalWithOnlyZIndex>
  );
});

const Button = memo(function Button({
  children,
  onClick,
}: {
  children: ReactNode | ReactNode[];
  onClick?(): void | Promise<void>;
}) {
  return (
    <button className={modal.buttonPrimary} onClick={onClick} type="button">
      {children}
    </button>
  );
});

export const RedoModal = memo(function RedoModal({
  open,
  onAdd,
  onClose,
  modalTextAdjustments,
  modalLogo,
  returnsCoverage,
  packageProtectionPlusEnabled,
  packageProtectionEnabled,
  modalCustomizationsV2,
  version,
  isPreview,
  enableTopLayerModalApi,
  returnsCoverageEnabled,
  finalSaleReturnsCoverageEnabled,
  returnPolicyUrl,
}: {
  open: boolean;
  onAdd?(): void | Promise<void>;
  onClose?: () => void;
  returnsCoverage?: Coverage;
  packageProtectionEnabled?: boolean;
  packageProtectionPlusEnabled?: boolean;
  modalTextAdjustments?: {
    text1: string;
    title1: string;
    text2: string;
    title2: string;
    text3: string;
    title3: string;
  };
  modalLogo?: string;
  modalCustomizationsV2?: InfoModal;
  version?: InfoModalVersion;
  isPreview?: boolean;
  enableTopLayerModalApi?: boolean;
  returnsCoverageEnabled: boolean;
  finalSaleReturnsCoverageEnabled: boolean;
  returnPolicyUrl?: string;
}) {
  if (version === InfoModalVersion.V2) {
    return (
      <ModalContentV2ImageWrapper
        customCss={modalCustomizationsV2?.customCss}
        enableTopLayerModalApi={enableTopLayerModalApi}
        isPreview={isPreview}
        onClose={onClose}
        open={open}
        sideImage={modalCustomizationsV2?.sideImage}
      >
        <ModalContentV2
          finalSaleReturnsCoverageEnabled={finalSaleReturnsCoverageEnabled}
          modalCustomizationsV2={modalCustomizationsV2}
          packageProtectionEnabled={packageProtectionEnabled}
          packageProtectionPlusEnabled={packageProtectionPlusEnabled}
          returnPolicyUrl={returnPolicyUrl}
          returnsCoverage={returnsCoverage}
          returnsCoverageEnabled={returnsCoverageEnabled}
        />
      </ModalContentV2ImageWrapper>
    );
  }

  return (
    <Modal
      enableTopLayerModalApi={enableTopLayerModalApi}
      onClose={onClose}
      open={open}
    >
      <div className={modal.redoModal}>
        <ModalContent
          modalLogo={modalLogo}
          modalTextAdjustments={modalTextAdjustments}
          onAdd={onAdd}
          onClose={onClose}
          packageProtectionPlusEnabled={packageProtectionPlusEnabled}
          returnPolicyUrl={returnPolicyUrl}
        />
      </div>
    </Modal>
  );
});

export const ModalContent = memo(function ModalContent({
  packageProtectionPlusEnabled,
  modalTextAdjustments,
  modalLogo,
  onAdd,
  onClose,
  returnPolicyUrl,
}: {
  packageProtectionPlusEnabled?: boolean;
  modalTextAdjustments?: {
    text1: string;
    title1: string;
    text2: string;
    title2: string;
    text3: string;
    title3: string;
  };
  modalLogo?: string;
  onClose?(): void;
  onAdd?(): void | Promise<void>;
  returnPolicyUrl?: string;
}) {
  const onButtonClick = useHandler(async () => {
    await onAdd?.();
    onClose?.();
  });

  const onCloseClick = useHandler(() => {
    onClose?.();
  });

  const translation = getTosTranslation();

  return (
    <>
      <div className={modal.modalLogo}>
        {modalLogo && (
          <img alt="Logo" className={modal.modalImageLogo} src={modalLogo} />
        )}
      </div>
      {onClose && (
        <div className={modal.modalCloseButton} onPointerUp={onCloseClick}>
          <ModalCloseButton />
        </div>
      )}

      <div className={modal.modalFeatures}>
        <Feature
          icon={<CheckCircle />}
          title={modalTextAdjustments?.title1 || "Add Redo"}
        >
          {modalTextAdjustments?.text1 ||
            "Qualifies you for FREE Returns if needed"}
        </Feature>
        <Feature
          icon={<Flag />}
          title={modalTextAdjustments?.title2 || "Receive your item"}
        >
          {modalTextAdjustments?.text2 || "Try it out"}
        </Feature>
        <Feature
          icon={<Exchange />}
          title={
            modalTextAdjustments?.title3 ||
            "Send it back for FREE if it doesn't work"
          }
        >
          {modalTextAdjustments?.text3 || "Quick and simple, at no extra cost"}
        </Feature>
      </div>

      {onAdd && (
        <div className={modal.modalAction}>
          <Button onClick={onButtonClick}>Add It</Button>
        </div>
      )}

      <div className={modal.modalFooter}>
        {translation.byPurchasing}{" "}
        <a
          className={modalV2.link}
          href="https://www.getredo.com/privacy-policy"
          rel="noreferrer"
          target="_blank"
        >
          {translation.privacyPolicy}
        </a>{" "}
        {translation.and}{" "}
        <a
          className={modalV2.link}
          href="https://www.getredo.com/terms-conditions"
          rel="noreferrer"
          target="_blank"
        >
          {translation.termsAndConditions}
        </a>
        . <ReturnPolicyClause translation={translation} url={returnPolicyUrl} />
        {packageProtectionPlusEnabled && (
          <>
            {" "}
            {translation.shippingProtectionProvidedBy}{" "}
            <a
              className={modalV2.link}
              href="https://shipsurance.com/redo/terms"
              rel="noreferrer"
              target="_blank"
            >
              Shipsurance
            </a>
            .
          </>
        )}
      </div>
    </>
  );
});

const Feature = memo(function Feature({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={modal.feature}>
      <div className={modal.featureBody}>
        <div className={modal.featureIcon}>{icon}</div>
        <h1 className={modal.featureTitle}>{title}</h1>
        <div className={modal.featureSummary}>{children}</div>
      </div>
    </section>
  );
});

export const V2ImageWrapper = memo(function V2ImageWrapper({
  children,
  onClose,
  closeButtonRef,
  sideImage,
  isExtendedWarranty,
  customCss,
  isPreview,
  foundSelector,
  containerRef,
  enableTopLayerModalApi,
}: {
  children: ReactNode;
  onClose?(): void;
  closeButtonRef?: Ref<HTMLButtonElement>;
  sideImage?: string;
  isExtendedWarranty?: boolean;
  customCss?: string;
  isPreview?: boolean;
  foundSelector?: string;
  containerRef?: React.Ref<HTMLDivElement>;
  enableTopLayerModalApi?: boolean;
}) {
  // The top-layer path wraps this in a native <dialog> (which already carries
  // dialog/aria-modal semantics), so only declare them here for the z-index
  // path — otherwise assistive tech sees two nested dialogs for one modal.
  const isDialog = !isPreview && !enableTopLayerModalApi;
  const accessibilityTranslation = getAccessibilityTranslation();
  return (
    <>
      {customCss && <style>{customCss}</style>}
      {foundSelector && <meta found-selector={foundSelector} />}
      <div
        aria-modal={isDialog ? true : undefined}
        className={modalV2.redoModal}
        data-target="redo-modal-container"
        ref={containerRef}
        role={isDialog ? "dialog" : undefined}
        tabIndex={-1}
      >
        <div className={modalV2.modalContent} data-target="redo-modal-content">
          {onClose && !isPreview && (
            <button
              aria-label={accessibilityTranslation.close}
              className={modalV2.modalCloseButton}
              data-target="redo-modal-close-button"
              onClick={onClose}
              onPointerUp={onClose}
              ref={closeButtonRef}
              type="button"
            >
              <ModalCloseButton aria-hidden />
            </button>
          )}
          {sideImage && (
            <div
              className={classNames(
                modalV2.modalSideImage,
                isExtendedWarranty && modalV2.extendedWarranty,
              )}
              data-target="redo-modal-side-image"
              style={{
                backgroundImage: `url(${sideImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}
          <div
            className={classNames(
              modalV2.modalContentMain,
              isExtendedWarranty && modalV2.extendedWarranty,
            )}
            data-target="redo-modal-content-main"
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
});

export const ModalContentV2ImageWrapper = memo(
  function ModalContentImageWrapper({
    open,
    children,
    onClose,
    sideImage,
    isExtendedWarranty,
    customCss,
    isPreview,
    foundSelector,
    enableTopLayerModalApi,
  }: {
    open: boolean;
    children: ReactNode;
    onClose?(): void;
    sideImage?: string;
    isExtendedWarranty?: boolean;
    customCss?: string;
    isPreview?: boolean;
    foundSelector?: string;
    enableTopLayerModalApi?: boolean;
  }) {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    useManageEscapeKey({ active: !isPreview, onClose, open });

    if (isPreview) {
      return (
        <div className={classNames(modalV2.modal, modalV2.preview)}>
          <V2ImageWrapper
            customCss={customCss}
            isExtendedWarranty={isExtendedWarranty}
            isPreview={isPreview}
            onClose={onClose}
            sideImage={sideImage}
          >
            {children}
          </V2ImageWrapper>
        </div>
      );
    }
    return (
      <Modal
        disableFocusManagement={!enableTopLayerModalApi}
        enableTopLayerModalApi={enableTopLayerModalApi}
        initialFocusRef={closeButtonRef}
        onClose={onClose}
        open={open}
      >
        <V2ImageWrapper
          closeButtonRef={closeButtonRef}
          customCss={customCss}
          enableTopLayerModalApi={enableTopLayerModalApi}
          isExtendedWarranty={isExtendedWarranty}
          isPreview={isPreview}
          onClose={onClose}
          sideImage={sideImage}
        >
          {children}
        </V2ImageWrapper>
      </Modal>
    );
  },
);

export const ModalLogo = memo(function ModalLogo({ logo }: { logo?: string }) {
  return (
    <div className={modalV2.modalLogo} data-target="redo-modal-logo">
      {logo && <img alt="Logo" className={modal.modalImageLogo} src={logo} />}
    </div>
  );
});

export const ModalContentV2 = memo(function ModalContentV2({
  returnsCoverage,
  packageProtectionPlusEnabled,
  packageProtectionEnabled,
  modalCustomizationsV2,
  returnsCoverageEnabled,
  finalSaleReturnsCoverageEnabled,
  returnPolicyUrl,
}: {
  returnsCoverage?: Coverage;
  packageProtectionPlusEnabled?: boolean;
  packageProtectionEnabled?: boolean;
  modalCustomizationsV2?: InfoModal;
  returnsCoverageEnabled: boolean;
  finalSaleReturnsCoverageEnabled: boolean;
  returnPolicyUrl?: string;
}) {
  const returnsBullet = getReturnsBullet({
    finalSaleReturnsCoverageEnabled,
    modalCustomizationsV2,
    returnsCoverage,
    returnsCoverageEnabled,
  });
  const packageProtectionBullet = getPackageProtectionBullet({
    modalCustomizationsV2,
    packageProtectionEnabled,
  });

  const translation = getTosTranslation();

  return (
    <>
      <ModalLogo logo={modalCustomizationsV2?.logo} />
      <Text as="div" fontSize="xl" fontWeight="semibold">
        {HTMLReactParser(
          modalCustomizationsV2?.headerText || defaultHeaderText,
        )}
      </Text>
      <Text as="div" fontSize="sm" fontWeight="thin">
        {HTMLReactParser(
          modalCustomizationsV2?.subheaderText || defaultSubheaderText,
        )}
      </Text>
      <br />
      {returnsBullet && (
        <>
          <div className={modalV2.contentItem} data-target="Bullet-1">
            <div>
              <FeaturedRefreshIcon height={32} width={32} />
            </div>
            <div>
              <Text as="div" fontSize="md" fontWeight="semibold">
                {HTMLReactParser(returnsBullet.titleText)}
              </Text>
              <Text
                as="div"
                color="var(--redo-colors-gray-true-500)"
                fontSize="xs"
                fontWeight="thin"
              >
                {HTMLReactParser(returnsBullet.subtitleText)}
              </Text>
            </div>
          </div>
          <br />
        </>
      )}
      {packageProtectionBullet && (
        <>
          <div className={modalV2.contentItem} data-target="Bullet-2">
            <div>
              <FeaturedPackageCheckIcon height={32} width={32} />
            </div>
            <div>
              <Text as="div" fontSize="md" fontWeight="semibold">
                {HTMLReactParser(packageProtectionBullet.titleText)}
              </Text>
              <Text
                as="div"
                color="var(--redo-colors-gray-true-500)"
                fontSize="xs"
                fontWeight="thin"
              >
                {HTMLReactParser(packageProtectionBullet.subtitleText)}
              </Text>
            </div>
          </div>
          <br />
        </>
      )}
      <div className={modalV2.contentItem} data-target="Bullet-3">
        <div>
          <FeaturedLaptopIcon height={32} width={32} />
        </div>
        <div>
          <Text as="div" fontSize="md" fontWeight="semibold">
            {HTMLReactParser(
              modalCustomizationsV2?.line3?.titleText || bulletThreeTitleText,
            )}
          </Text>
          <Text
            as="div"
            color="var(--redo-colors-gray-true-500)"
            fontSize="xs"
            fontWeight="thin"
          >
            {HTMLReactParser(
              modalCustomizationsV2?.line3?.subtitleText ||
                bulletThreeSubtitleText,
            )}
          </Text>
        </div>
      </div>
      <br />
      {modalCustomizationsV2?.line4?.titleText && (
        <>
          <div className={modalV2.contentItem} data-target="Bullet-4">
            <div>
              <FeaturedLaptopIcon height={32} width={32} />
            </div>
            <div>
              <Text as="div" fontSize="md" fontWeight="semibold">
                {HTMLReactParser(modalCustomizationsV2.line4.titleText)}
              </Text>
              <Text
                as="div"
                color="var(--redo-colors-gray-true-500)"
                fontSize="xs"
                fontWeight="thin"
              >
                {HTMLReactParser(
                  modalCustomizationsV2.line4.subtitleText || "",
                )}
              </Text>
            </div>
          </div>
          <br />
        </>
      )}
      <br />

      <Text
        color="var(--redo-colors-gray-true-500)"
        fontSize="xs"
        fontWeight="thin"
      >
        {translation.byPurchasing}{" "}
        <a
          className={modalV2.link}
          href="https://www.getredo.com/privacy-policy"
          rel="noreferrer"
          target="_blank"
        >
          {translation.privacyPolicy}
        </a>{" "}
        {translation.and}{" "}
        <a
          className={modalV2.link}
          href="https://www.getredo.com/terms-conditions"
          rel="noreferrer"
          target="_blank"
        >
          {translation.termsAndConditions}
        </a>
        . <ReturnPolicyClause translation={translation} url={returnPolicyUrl} />
        {packageProtectionPlusEnabled && (
          <>
            {" "}
            {translation.shippingProtectionProvidedBy}{" "}
            <a
              className={modalV2.link}
              href="https://shipsurance.com/redo/terms"
              rel="noreferrer"
              target="_blank"
            >
              Shipsurance
            </a>
            .
          </>
        )}
      </Text>
    </>
  );
});
